import { useQuery } from '@tanstack/react-query';
import { serverRequest } from '../util/server_request';
import { extractErrorMessage } from './extractErrorMessage';
import { notifyUser } from '../util/util';

const notifiedErrors = new Set();

// Helper function to notify only once for the same error
const notifyOnce = (type, message, title = "") => {
  if (!notifiedErrors.has(message)) {
    notifiedErrors.add(message);
    notifyUser(type, message, title);
  }
};

// Pro-level: Check if error is retryable
const isRetryableError = (error) => {
  if (!error) return false;
  const statusCode = error.status || error.statusCode || error.code;
  if (statusCode >= 500 && statusCode < 600) return true;
  const msg = String(error.message || error.error || '').toLowerCase();
  return msg.includes('network') || msg.includes('timeout') || msg.includes('econnrefused');
};

const useGetPremiumStatement = (policyNumber, enabled, options = {}) => {
  const isValidPolicyNumber = policyNumber && typeof policyNumber === 'string' && policyNumber.trim() !== '';

  return useQuery({
    queryKey: ['premiumStatement', policyNumber],
    queryFn: async () => {
      if (!isValidPolicyNumber) {
        throw new Error('Invalid or missing policy number');
      }

      try {
        // Build query parameters using filter[policy_number] format only (no year filter)
        const params = new URLSearchParams();
        params.append('filter[policy_number]', policyNumber);
        
        const endpoint = `generated-policy-reports?${params.toString()}`;
        const response = await serverRequest('GET', endpoint);
        
        // Handle API errors
        if (response?.error) {
          const errorMessages = {
            "You have exceeded your Requests.": {
              message: 'Request limit exceeded. Please try again later.',
              type: 'warning',
              title: 'Rate Limit'
            },
            "Not Found.": {
              message: 'The requested reports could not be found.',
              type: 'error',
              title: 'Not Found'
            }
          };

          const errorConfig = errorMessages[response.error] || {
            message: response.error,
            type: 'error',
            title: 'Error'
          };

          notifyOnce(errorConfig.type, errorConfig.message, errorConfig.title);
          throw new Error(errorConfig.message);
        }

        if (!response?.success) {
          const errorMessage = response?.message || 'Failed to initiate policy report';
          notifyOnce('error', errorMessage, 'Error');
          throw new Error(errorMessage);
        }

        // Pro-level: Normalize response data - data is an array of reports
        const normalizedResponse = {
          ...response,
          data: Array.isArray(response.data)
            ? response.data.map(report => ({
                ...report,
                id: report.id || report.report_id,
                report_id: report.report_id || report.id,
              }))
            : response.data ? [{
                ...response.data,
                id: response.data.id || response.data.report_id,
                report_id: response.data.report_id || response.data.id,
              }] : []
        };

        return normalizedResponse;
        
      } catch (error) {
        if (error?.message?.toLowerCase().includes('exceeded your requests')) {
          const limitMessage = 'Request limit exceeded. Please try again later.';
          notifyOnce("warning", limitMessage, "Rate Limit");
          throw new Error(limitMessage);
        }
        
        const errorMessage = extractErrorMessage(error, 'Failed to fetch premium statement');
        notifyOnce("error", errorMessage, "Error");
        throw new Error(errorMessage);
      }
    },
    enabled: enabled && isValidPolicyNumber,
    retry: (failureCount, error) => {
      // Only retry on retryable errors
      if (!isRetryableError(error)) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30 * 1000, // 30 seconds
    cacheTime: 5 * 60 * 1000, // 5 minutes
    ...options, // Allow overriding defaults
  });
};

export { useGetPremiumStatement };
